import { BuildingIcon, CheckIcon, ChevronDownIcon, TriangleAlert } from 'lucide-react'
import React from 'react'
import { toast } from 'sonner'

import { useEmployerSwitchSync } from 'hooks/useEmployerSwitchSync'
import { DropdownMenu } from 'slab'
import { useGetEmployersEmployers, usePostEmployersEmployersSetCurrentEmployer } from 'utils/web_api'
import { useWindowLocation } from 'utils/windowLocation'

const EmployerSwitcher = () => {
  const { reload } = useWindowLocation()

  const { data: employers, isLoading, error } = useGetEmployersEmployers()
  const setCurrentEmployerMutation = usePostEmployersEmployersSetCurrentEmployer()
  const { notifyOtherTabs } = useEmployerSwitchSync()

  // Find the current employer
  const currentEmployer = employers?.find(
    (employer) => employer.is_current_employer
  )

  // Filter out the current employer from the dropdown options
  const otherEmployers = employers?.filter(
    (employer) => !employer.is_current_employer
  ) || []

  // I wanted to use the Button component from slab, but it doesn't support truncating the text or aligning the icons
  const trigger = (
    <button
      className='flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full max-w-full'
      disabled={isLoading}
    >
      <BuildingIcon className='w-4 h-4 flex-shrink-0' aria-hidden />
      <span className='truncate min-w-0 flex-1'>
        {isLoading
          ? 'Loading...'
          : currentEmployer
            ? currentEmployer.name
            : 'Select Employer'}
      </span>
      <ChevronDownIcon className='w-4 h-4 flex-shrink-0' aria-hidden />
    </button>
  )

  const handleEmployerSelect = async (employerId: string) => {
    await setCurrentEmployerMutation.mutateAsync({
      data: { employer_id: employerId }
    })
      .then(() => {
        // Notify other tabs before reloading
        notifyOtherTabs(employerId)

        // Reload the page on success
        reload()
      })
      .catch((error: Error) => {
        console.error('Failed to switch employers:', error)
        toast.error(
          'Failed to switch employers. If this issue persists, please contact support.',
          { dismissible: true, icon: <TriangleAlert className='w-5 h-5 text-yellow-400' />, position: 'top-right' })
      })
  }

  if (error) {
    return (
      <div className='text-red-600 text-sm'>
        Error loading employers. Please try again.
      </div>
    )
  }

  return (
    <DropdownMenu
      trigger={trigger}
      align='start'
      zIndex={9999}
      withPortal={false}
    >
      {isLoading
        ? (
          <DropdownMenu.Item disabled>
            Loading employers...
          </DropdownMenu.Item>
          )
        : !employers || employers.length === 0
            ? (
              <DropdownMenu.Item disabled>
                No employers available
              </DropdownMenu.Item>
              )
            : (
              <>
                {/* Current employer - disabled and with checkmark */}
                {currentEmployer && (
                  <DropdownMenu.Item
                    key={currentEmployer.id}
                    leftIcon={BuildingIcon}
                    rightIcon={CheckIcon}
                    disabled
                  >
                    <div className='flex flex-col items-start'>
                      <span className='font-medium'>{currentEmployer.name}</span>
                      {currentEmployer.dba && (
                        <span className='text-xs text-gray-500'>
                          DBA: {currentEmployer.dba}
                        </span>
                      )}
                      <span className='text-xs text-blue-600 font-medium'>
                        Current
                      </span>
                    </div>
                  </DropdownMenu.Item>
                )}

                {/* Other employers */}
                {otherEmployers.length > 0
                  ? (
                      otherEmployers.map((employer) => (
                        <DropdownMenu.Item
                          key={employer.id}
                          leftIcon={BuildingIcon}
                          onSelect={() => handleEmployerSelect(employer.id)}
                        >
                          <div className='flex flex-col items-start'>
                            <span className='font-medium'>{employer.name}</span>
                            {employer.dba && (
                              <span className='text-xs text-gray-500'>
                                DBA: {employer.dba}
                              </span>
                            )}
                          </div>
                        </DropdownMenu.Item>
                      ))
                    )
                  : (
                    <DropdownMenu.Item disabled>
                      No other employers available
                    </DropdownMenu.Item>
                    )}
              </>
              )}
    </DropdownMenu>
  )
}

export default EmployerSwitcher
